import React, { useState, useEffect, useMemo } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useData } from '../contexts/DataContext'
import QuickAdd from '../components/QuickAdd'
import HistoryList from '../components/HistoryList'
import { ensureSubs, formatDate, renderPayload } from '../utils/helpers'

const Personal = () => {
  const { user } = useAuth()
  const { exercises, fetchExercises, results, fetchResults, createResult } = useData()
  const [view, setView] = useState({ mode: 'home', exerciseId: null })

  useEffect(() => {
    fetchExercises()
    if (user?.id) {
      fetchResults({ memberId: user.id })
    }
  }, [user])

  const myResults = useMemo(() => 
    results.filter(r => r.memberId._id === user?.id).sort((a, b) => 
      new Date(b.createdAt) - new Date(a.createdAt)
    ), [results, user?.id]
  )

  const recent7 = useMemo(() => {
    const sevenDaysAgo = new Date()
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7)
    return myResults.filter(r => new Date(r.createdAt) >= sevenDaysAgo)
  }, [myResults])

  const getExerciseById = (id) => exercises.find(e => e._id === id)

  const handleAddResult = async (resultData) => {
    const result = await createResult({
      memberId: user.id,
      exerciseId: resultData.exerciseId,
      subId: resultData.subId,
      payload: resultData.payload
    })
    
    if (result.success) {
      fetchResults({ memberId: user.id })
    } else {
      alert(result.error)
    }
  }

  if (view.mode === 'detail') {
    const exercise = getExerciseById(view.exerciseId)
    const subs = ensureSubs(exercise)

    return (
      <div className="exercise-detail">
        {/* Header */}
        <div className="exercise-detail-header">
          <div className="exercise-info">
            <button className="back-btn" onClick={() => setView({ mode: 'home' })}>
              ← Back
            </button>
            <div className="exercise-title-section">
              <h1 className="exercise-title">{exercise?.name}</h1>
              <div className="exercise-meta">
                <span className="exercise-mode-badge">{exercise?.mode}</span>
                {exercise?.mode === 'workout' && exercise?.desc && (
                  <span className="exercise-desc">{exercise.desc}</span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Exercise Categories */}
        <div className="exercise-categories">
          {subs.map(sub => {
            const subResults = myResults.filter(r => 
              r.exerciseId._id === exercise._id && r.subId === (sub._id || sub.id)
            )
            const last = subResults[0] || null

            return (
              <div key={sub._id || sub.id} className="category-card">
                <div className="category-header">
                  <h3 className="category-name">{sub.name}</h3>
                  <div className="category-fields">
                    {exercise.mode === 'strength' ? 'Weight • Reps • Sets • Notes' :
                     exercise.mode === 'cardio' ? (exercise.cardioMetric === 'distance' ? 'Distance • Time • Notes' : 'Calories • Time • Notes') :
                     Object.entries(sub?.fields || {}).filter(([k, v]) => v !== false).map(([k]) => {
                       if (k === 'primary') return exercise.cardioMetric === 'distance' ? 'Distance' : 'Calories'
                       return k[0].toUpperCase() + k.slice(1)
                     }).join(' • ')}
                  </div>
                </div>

                <div className="quick-add-section">
                  <QuickAdd
                    exercise={exercise}
                    sub={sub}
                    last={last}
                    onAdd={(payload) => handleAddResult({
                      exerciseId: exercise._id,
                      subId: sub._id || sub.id,
                      payload
                    })}
                  />
                </div>

                <div className="last-entry">
                  {last ? (
                    <div className="last-entry-content">
                      <span className="last-label">Last workout:</span>
                      <span className="last-value">{renderPayload(last.payload, exercise)}</span>
                      <span className="last-date">{formatDate(last.createdAt)}</span>
                    </div>
                  ) : (
                    <div className="no-entry">
                      <span className="no-entry-icon">🎯</span>
                      <span className="no-entry-text">Ready for your first session?</span>
                    </div>
                  )}
                </div>
                
                <div className="history-section">
                  <HistoryList rows={subResults.slice(0, 5)} exercise={exercise} topWeight={0} onDelete={() => {}} />
                  {subResults.length > 5 && (
                    <div className="view-more">
                      <button className="view-more-btn">View all {subResults.length} entries</button>
                    </div>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  return (
    <div className="personal-dashboard">
      {/* Welcome Header */}
      <div className="personal-header">
        <div className="welcome-section">
          <h1 className="welcome-title">Welcome back, {user?.name}!</h1>
          <p className="welcome-subtitle">Ready to crush your fitness goals today?</p>
        </div>
        <div className="stats-overview">
          <div className="stat-card">
            <div className="stat-number">{recent7.length}</div>
            <div className="stat-label">This Week</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{myResults.length}</div>
            <div className="stat-label">Total Sessions</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{exercises.length}</div>
            <div className="stat-label">Exercises</div>
          </div>
        </div>
      </div>

      <div className="grid two">
        {/* Left side - Recent activity */}
        <div className="personal-card recent-activity">
          <div className="card-header">
            <h2 className="card-title">
              <span className="card-icon">🔥</span>
              Recent Activity
            </h2>
            <div className="card-subtitle">Your last 7 days</div>
          </div>
          
          <div className="activity-list">
            {recent7.length ? recent7.map(result => {
              const exercise = getExerciseById(result.exerciseId._id)
              return (
                <div key={result._id} className="activity-item">
                  <div className="activity-content">
                    <div className="activity-exercise">{exercise?.name}</div>
                    <div className="activity-details">{renderPayload(result.payload, exercise)}</div>
                    <div className="activity-date">{formatDate(result.createdAt)}</div>
                  </div>
                  <div className="activity-indicator"></div>
                </div>
              )
            }) : (
              <div className="empty-state">
                <div className="empty-icon">💪</div>
                <div className="empty-message">No workouts this week yet</div>
                <div className="empty-subtitle">Time to get started!</div>
              </div>
            )}
          </div>
        </div>

        {/* Right side - Exercises */}
        <div className="personal-card exercises-list">
          <div className="card-header">
            <h2 className="card-title">
              <span className="card-icon">⚡</span>
              Your Exercises
            </h2>
            <div className="card-subtitle">Choose your workout</div>
          </div>
          
          <div className="exercises-grid">
            {exercises.map(exercise => (
              <div 
                key={exercise._id} 
                className="exercise-card"
                onClick={() => setView({ mode: 'detail', exerciseId: exercise._id })}
              >
                <div className="exercise-content">
                  <div className="exercise-name">{exercise.name}</div>
                  <div className="exercise-type">{exercise.mode}</div>
                </div>
                <div className="exercise-arrow">→</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export default Personal
